import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { InteractiveElementShell } from "@/components/interactive/InteractiveElementShell";
import SelfReflectionRunner, {
  type SelfReflectionRunnerState,
} from "@/components/storyboard/selfreflection/SelfReflectionRunner";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useInteractiveElementState } from "@/hooks/useInteractiveElementState";
import { useReportInteractiveLearningProgress } from "@/hooks/useReportInteractiveLearningProgress";
import {
  getInteractiveElementState,
  setInteractiveElementState,
  InteractiveElementStatusKind,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import type { InteractiveIllustrationKind } from "@/lib/interactiveIllustration";
import type { SelfReflectionAnswerRecord } from "@/lib/selfReflectionAnswers";
import { useSelfReflectionValidations } from "@/hooks/useSelfReflectionValidations";
import type { TextSelfReflectionQuestionsData } from "@/types/storyboard";

export interface SelfReflectionFlowLabels {
  startCta: string;
  continueCta: string;
  completedText: string;
}

const DEFAULT_LABELS: SelfReflectionFlowLabels = {
  startCta: "Mit der Selbstreflexion starten",
  continueCta: "Mit der Selbstreflexion weitermachen",
  completedText: "Du hast diese Selbstreflexion abgeschlossen.",
};

export interface SelfReflectionInteractiveFlowProps {
  id: string;
  title: string;
  data: TextSelfReflectionQuestionsData;
  onValidate: (
    answer: string,
    index: number
  ) => Promise<{ correct: boolean | null; feedback: string }>;
  labels?: SelfReflectionFlowLabels;
  illustrationKind?: InteractiveIllustrationKind;
}

const IN_PROGRESS: InteractiveElementStatus = {
  kind: InteractiveElementStatusKind.InProgress,
  partial: null,
};
const COMPLETED: InteractiveElementStatus = {
  kind: InteractiveElementStatusKind.Completed,
  attempts: 1,
  completedAt: "",
};

const hasSubmittedAnswer = (partial: SelfReflectionRunnerState): boolean =>
  Object.values(partial.answers).some((answer) => answer.submitted);

/**
 * Self- and experience-reflection in the shared interactive-element shell —
 * inline in the storyboard card. After the last answer, the read-only review
 * opens on the card directly (no Fertig step — feedback is not shown twice).
 *
 * Enrollment learning progress posts completed only when the last answer is
 * evaluated — scrolling past the block does not finish the element.
 */
const SelfReflectionInteractiveFlow = ({
  id,
  title,
  data,
  onValidate,
  labels = DEFAULT_LABELS,
  illustrationKind = "self_reflection",
}: SelfReflectionInteractiveFlowProps) => {
  const { enrollmentId } = useCourseDetailStore();
  const backend = useSelfReflectionValidations(id, data.questions?.length ?? 0);
  const completedRef = useRef(false);

  const [sessionStatus, setSessionStatus] =
    useState<InteractiveElementStatus | null>(null);
  const [sessionPartial, setSessionPartial] =
    useState<SelfReflectionRunnerState | null>(null);
  const [sessionRecord, setSessionRecord] =
    useState<SelfReflectionAnswerRecord | null>(null);

  const persistPartialRef = useRef<
    (partial: SelfReflectionRunnerState, index: number) => void
  >(() => undefined);

  const saveProgressSoLearnerCanResume = () => {
    persistPartialRef.current = (partial, index) => {
      setSessionPartial(partial);

      const learnerHasNotSubmittedAnAnswer = !hasSubmittedAnswer(partial);
      const learnerAlreadyFinished = completedRef.current;
      if (learnerHasNotSubmittedAnAnswer || learnerAlreadyFinished) return;

      const learnerAlreadyCompletedThisReflection =
        getInteractiveElementState(id, enrollmentId).kind ===
        InteractiveElementStatusKind.Completed;
      if (learnerAlreadyCompletedThisReflection) return;

      setSessionStatus(IN_PROGRESS);
      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.InProgress,
        partial,
        lastTaskIndex: index,
      });
    };
  };

  useEffect(saveProgressSoLearnerCanResume, [id, enrollmentId]);

  const onPartialChangeStable = useRef(
    (partial: SelfReflectionRunnerState, index: number) => {
      persistPartialRef.current(partial, index);
    }
  ).current;

  useReportInteractiveLearningProgress(id, { enabled: true });

  const { status: localStatus } = useInteractiveElementState(id, enrollmentId);
  const localResume =
    localStatus.kind === InteractiveElementStatusKind.InProgress
      ? (localStatus.partial as SelfReflectionRunnerState | null)
      : null;

  const status = sessionStatus ?? (backend.loaded ? backend.status : undefined);
  const reviewRecord = sessionRecord ?? backend.reviewRecord;
  const initialPartial =
    sessionPartial ?? localResume ?? backend.initialPartial;
  // Runner seeds from useState on first mount — wait until local in-progress
  // exists or validation_data has settled so resume does not start at Q1.
  const seedReady = initialPartial != null || backend.loaded;

  return (
    <InteractiveElementShell
      contentElementId={id}
      enrollmentId={enrollmentId}
      title={title}
      illustrationKind={illustrationKind}
      layout="inline"
      statusOverride={status}
      notStartedCta={{ label: labels.startCta }}
      inProgressCta={{ label: labels.continueCta }}
      supportsInProgress
      completedView={() => {
        if (reviewRecord) {
          return (
            <SelfReflectionRunner
              data={data}
              onValidate={onValidate}
              readOnly
              reviewRecord={reviewRecord}
            />
          );
        }

        return (
          <div className="flex w-full flex-col items-center gap-4 text-center">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="size-5" />
              <span className="font-semibold">{labels.completedText}</span>
            </div>
          </div>
        );
      }}
      editingView={({ complete, close }) => {
        if (!seedReady) return null;

        return (
          <SelfReflectionRunner
            data={data}
            onValidate={onValidate}
            initialPartial={initialPartial}
            onPartialChange={onPartialChangeStable}
            onComplete={(record) => {
              completedRef.current = true;
              setSessionRecord(record);
              complete(undefined, record);
              setSessionStatus(COMPLETED);
              // Skip the shell's post-complete Fertig step — the read-only
              // review already includes answers and feedback.
              close();
            }}
          />
        );
      }}
    />
  );
};

export default SelfReflectionInteractiveFlow;
